import React from 'react';
import moment from 'moment-timezone';
import humanizeDuration from 'humanize-duration';
import { FormattedMessage } from 'react-intl';
import getLocale from 'utils/locale';

export function calcDuration({
  created,
  terminated,
  locale = 'en',
  config = {},
  displayZero,
}) {
  const startMoment = moment(created);
  const endMoment = terminated ? moment(terminated) : moment();
  let diff = endMoment.diff(startMoment);
  if (displayZero && !diff && created && terminated) {
    // to display zero minute if created and terminated is at the same time.
    diff = 1;
  }
  return diff
    ? humanizeDuration(diff, {
        language: locale,
        units: ['d', 'h', 'm'],
        round: true,
        ...config,
      })
    : null;
}

// Separated getting for easier unit-testing:
export function getApproximateShortTimeProps(timeInMinutes) {
  // eslint-disable-next-line no-restricted-properties
  if (!timeInMinutes || window.isNaN(timeInMinutes) || timeInMinutes < 1) {
    return { id: 'SHORT_DURATION_MINUTES', values: { minutes: 0 } };
  }

  const MINUTES_PER_HOUR = 60;
  const MINUTES_PER_DAY = 24 * 60;
  const MINUTES_PER_YEAR = 24 * 60 * 365;

  const hours = Math.floor(timeInMinutes / MINUTES_PER_HOUR);
  if (hours < 2) {
    return { id: 'SHORT_DURATION_MINUTES', values: { minutes: timeInMinutes } };
  }
  const days = Math.floor(timeInMinutes / MINUTES_PER_DAY);
  if (days < 2) {
    return { id: 'SHORT_DURATION_HOURS', values: { hours } };
  }
  const years = Math.floor(timeInMinutes / MINUTES_PER_YEAR);
  if (years === 0) {
    return { id: 'SHORT_DURATION_DAYS', values: { days } };
  }
  return { id: 'SHORT_DURATION_YEARS', values: { years } };
}

export function changeTimezone(date, newTimezone) {
  return moment.tz(
    date.format('YYYY-MM-DDTHH:mm:ss'),
    moment.ISO_8601,
    newTimezone,
  );
}

export function toFormattedHHmmss(timeInSeconds) {
  if (!timeInSeconds) {
    return '0s';
  }

  const sec = parseInt(timeInSeconds, 10);
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec - hours * 3600) / 60);
  const seconds = sec - hours * 3600 - minutes * 60;

  if (hours && hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes && minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

// should really be a component
export function getApproximateShortTime(timeInMinutes) {
  return <FormattedMessage {...getApproximateShortTimeProps(timeInMinutes)} />;
}

/**
 * Given a duration in seconds, format them into "**d **h **m" format.
 *
 * @param {number|string} timeInSeconds=undefined - The duration in seconds. If this is a string, it'll be parsed to a number
 * @param {object} config - the config object
 * @property {boolean} roundUpMinutes=false - Because we only format down to minutes, sometimes it makes sense to round
 *    up the minute, not round down. For example, "12 min and 15 sec" might make sense to be rounded as "13 min",
 *    not "12 min" in some scenarios.
 * @returns {string} - the formatted human-readable string.
 */
export function toFormattedDDhhmm(
  timeInSeconds,
  config = { roundUpMinutes: false },
) {
  if (!timeInSeconds) {
    return '0m';
  }

  const sec = parseInt(timeInSeconds, 10);
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec - days * 86400) / 3600);

  const minutesBeforeRounding = (sec - days * 86400 - hours * 3600) / 60;
  const minutes = config.roundUpMinutes
    ? Math.ceil(minutesBeforeRounding)
    : Math.floor(minutesBeforeRounding);

  if (days && days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours && hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

/**
 * Given a duration in milliseconds, format them into "**d **h **m **s" format.
 *
 * @param {number|string} timeInMillis=undefined - The duration in milliseconds. If this is a string, it'll be parsed to a number
 *
 * @returns {string} - the formatted human-readable string.
 */
export const toFormattedDDhhmmss = timeInMillis => {
  if (!timeInMillis) {
    return '-';
  }

  if (timeInMillis < 1000) {
    return '<1s';
  }

  const seconds = parseInt(timeInMillis, 10) / 1000;
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const daysFormatted = days > 0 ? `${days}d` : '';
  const hoursFormatted = hours > 0 ? `${hours}h` : '';
  const minsFormatted = mins > 0 ? `${mins}m` : '';
  const secsFormatted = secs > 0 ? `${secs}s` : '';

  return [daysFormatted, hoursFormatted, minsFormatted, secsFormatted]
    .filter(value => value !== '')
    .join(' ');
};

export function toDate(timestamp, timezone) {
  return moment(timestamp).tz(timezone).locale(getLocale());
}

export function startOfDay(timezone) {
  return moment().startOf('day').tz(timezone).locale(getLocale());
}

export function toDateDisplay(timestamp, timezone, formatter) {
  return toDate(timestamp, timezone).format(formatter);
}

export function getMinTime(time1, time2) {
  if (time1 && time2) {
    return moment.min(moment(time1), moment(time2)).toISOString();
  }
  return time1 || time2;
}

export function displayIfLessThan(lowerBound, displayOption, timeInSeconds) {
  return timeInSeconds < lowerBound
    ? displayOption
    : toFormattedHHmmss(Math.floor(timeInSeconds / lowerBound));
}

export function formatTimeZone(timezone, intl) {
  return moment.tz.zone(timezone)
    ? `(UTC${moment()
        .tz(timezone)
        .format('ZZ')}) ${intl?.formatMessage({ id: timezone }) ?? timezone}`
    : timezone;
}
